import React, { useCallback, useEffect, useState } from 'react';
import { CalendarClock, Clock, Copy, Link2, Loader2 } from 'lucide-react';
import { Switch } from '@/components/ui';
import { useOnboarding, useStepSaver, useStepValidity } from '../../../contexts/OnboardingContext';
import { CompanyWorkScheduleService } from '../../../api/services/CompanyWorkScheduleService';
import { System_DayOfWeek } from '../../../api/models/System_DayOfWeek';
import type { Routemize_DTOs_UpdateCompanyWorkScheduleDTO } from '../../../api/models/Routemize_DTOs_UpdateCompanyWorkScheduleDTO';
import { ONBOARDING_STEP } from '../../../business/onboarding/OnboardingManager';
import {
  applyDayScheduleChange,
  DEFAULT_SCHEDULE,
  isWeekend,
  SCHEDULE_PRESETS,
  shouldSyncWeekdays,
  type DaySchedule,
} from '../../../business/onboarding/workSchedule';
import { formatTimeString } from '../../../utils/timeUtils';
import { Callout, StepIntro } from '../shared/StepShell';

const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const value = `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`;
  return { value, label: formatTimeString(value) };
});

/**
 * Existing schedules aren't guaranteed to sit on a 15-minute boundary. Splice
 * any off-grid value into the list so the select shows the real time instead of
 * rendering blank and silently rewriting it on the next save.
 */
const timeOptionsWith = (current: string) =>
  TIME_OPTIONS.some(o => o.value === current)
    ? TIME_OPTIONS
    : [...TIME_OPTIONS, { value: current, label: formatTimeString(current) }].sort((a, b) =>
        a.value.localeCompare(b.value)
      );

const selectClass =
  'h-9 min-w-0 flex-1 rounded-lg border border-ink-200 bg-ink-50 px-2 text-sm text-ink-900 transition-colors hover:border-ink-300 focus:outline-none focus-visible:border-accent-500 focus-visible:ring-2 focus-visible:ring-accent-500/40 disabled:opacity-50';

export const HoursStep: React.FC = () => {
  const { completeStep } = useOnboarding();

  const [isLoading, setIsLoading] = useState(true);
  const [schedule, setSchedule] = useState<DaySchedule[]>(DEFAULT_SCHEDULE);
  const [syncEnabled, setSyncEnabled] = useState(true);

  useEffect(() => {
    let cancelled = false;

    CompanyWorkScheduleService.getApiAppCompanyWorkScheduleGetEnhancedWorkSchedule()
      .then(scheduleData => {
        if (cancelled || !scheduleData?.dailySchedules?.length) return;

        const loaded = DEFAULT_SCHEDULE.map(day => {
          const match = scheduleData.dailySchedules?.find(s => s.dayOfWeek === day.dayOfWeek);
          if (!match) return day;
          return {
            ...day,
            id: match.id,
            enabled: match.isWorkDay ?? day.enabled,
            startTime: match.startTime?.substring(0, 5) || day.startTime,
            endTime: match.endTime?.substring(0, 5) || day.endTime,
            lunchStartTime: match.lunchStartTime ?? null,
            lunchEndTime: match.lunchEndTime ?? null,
          };
        });
        setSchedule(loaded);
        setSyncEnabled(shouldSyncWeekdays(loaded));
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const workingDays = schedule.filter(d => d.enabled);
  const hasInvalidRange = schedule.some(d => d.enabled && d.startTime >= d.endTime);

  useStepValidity(workingDays.length > 0 && !hasInvalidRange);

  const updateDay = (index: number, patch: Partial<DaySchedule>) => {
    setSchedule(prev => applyDayScheduleChange(prev, index, patch, syncEnabled));
  };

  const applyPreset = (preset: (typeof SCHEDULE_PRESETS)[number]) => {
    // Presets set uniform weekday hours, so sync is meaningful again.
    setSyncEnabled(true);
    setSchedule(prev =>
      prev.map((day, index) => {
        // DEFAULT_SCHEDULE is Monday-first, so index 0 is Monday (ISO day 1).
        const isoDay = index + 1;
        const active = (preset.days as readonly number[]).includes(isoDay);
        return active
          ? { ...day, enabled: true, startTime: preset.startTime, endTime: preset.endTime }
          : { ...day, enabled: false };
      })
    );
  };

  const copyFirstDayToAll = () => {
    const source = workingDays[0];
    if (!source) return;
    setSchedule(prev =>
      prev.map(day =>
        day.enabled ? { ...day, startTime: source.startTime, endTime: source.endTime } : day
      )
    );
  };

  useStepSaver(
    ONBOARDING_STEP.HOURS,
    useCallback(async () => {
      // Resolve each day's row id at save time rather than trusting the ids
      // captured on mount. A day sent without an id is inserted rather than
      // updated, so a missing id creates a second row for that day — customers
      // ended up with every weekday listed twice (their hours plus an untouched
      // 9–5 set). The mount-time ids go missing whenever the initial load fails
      // or the user advances the wizard before it resolves.
      const existing =
        await CompanyWorkScheduleService.getApiAppCompanyWorkScheduleGetEnhancedWorkSchedule();
      const idByDayOfWeek = new Map<System_DayOfWeek, string>();
      for (const s of existing?.dailySchedules ?? []) {
        if (s.dayOfWeek === undefined || !s.id) continue;
        if (!idByDayOfWeek.has(s.dayOfWeek)) idByDayOfWeek.set(s.dayOfWeek, s.id);
      }

      const dailySchedules: Routemize_DTOs_UpdateCompanyWorkScheduleDTO[] = schedule.map(
        (day, index) => {
          const id = idByDayOfWeek.get(day.dayOfWeek) ?? day.id;
          return {
            ...(id ? { id } : {}),
            dayOfWeek: day.dayOfWeek,
            startTime: day.startTime,
            endTime: day.endTime,
            lunchStartTime: day.lunchStartTime ?? null,
            lunchEndTime: day.lunchEndTime ?? null,
            isWorkDay: day.enabled,
            displayOrder: index,
          };
        }
      );

      await CompanyWorkScheduleService.putApiAppCompanyWorkScheduleUpdateEnhancedWorkSchedule({
        dailySchedules,
      });

      completeStep(ONBOARDING_STEP.HOURS);
    }, [schedule, completeStep])
  );

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-accent-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <StepIntro>
        The outer limits of your working day — when you're on the clock, not the exact times
        customers can book.
      </StepIntro>

      <div className="flex flex-wrap gap-1.5">
        {SCHEDULE_PRESETS.map(preset => (
          // eslint-disable-next-line no-restricted-syntax -- compact chip in a preset row; Button's min-height is too tall here
          <button
            key={preset.label}
            type="button"
            onClick={() => applyPreset(preset)}
            className="rounded-lg border border-ink-200 bg-surface px-3 py-1.5 text-xs font-medium text-ink-600 transition-colors hover:border-ink-300 hover:bg-ink-50"
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-ink-200 bg-surface">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-ink-100 px-3 py-2.5">
          <label className="flex cursor-pointer items-center gap-2">
            <Switch
              size="sm"
              checked={syncEnabled}
              onCheckedChange={setSyncEnabled}
              aria-label="Same hours for all weekdays"
            />
            <span className="text-xs font-medium text-ink-700">Same hours Mon–Fri</span>
          </label>
          {workingDays.length > 1 && (
            // eslint-disable-next-line no-restricted-syntax -- inline text action in a toolbar row; Button adds too much weight
            <button
              type="button"
              onClick={copyFirstDayToAll}
              className="flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-accent-600 transition-colors hover:bg-accent-50"
            >
              <Copy className="h-3 w-3" />
              Match all to {workingDays[0].shortDay}
            </button>
          )}
        </div>

        <div className="divide-y divide-ink-100">
          {schedule.map((day, index) => {
            const invalid = day.enabled && day.startTime >= day.endTime;
            const synced = syncEnabled && !isWeekend(day.day) && day.enabled;
            return (
              <div
                key={day.day}
                className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5 ${
                  day.enabled ? 'bg-surface' : 'bg-ink-50'
                }`}
              >
                {/* Switch carries no shrink guard of its own, so both it and
                    the label need one — without it the longest day names
                    squeeze the toggle and clip the text. */}
                <div className="flex w-36 flex-shrink-0 items-center gap-2.5 sm:w-44">
                  <Switch
                    checked={day.enabled}
                    onCheckedChange={checked => updateDay(index, { enabled: checked })}
                    aria-label={`${day.day} working day`}
                    className="shrink-0"
                  />
                  <span
                    className={`whitespace-nowrap text-sm font-medium ${
                      day.enabled ? 'text-ink-900' : 'text-ink-400'
                    }`}
                  >
                    {day.day}
                  </span>
                  {synced && (
                    <Link2
                      className="h-3 w-3 flex-shrink-0 text-accent-400"
                      aria-label="Synced with the other weekdays"
                    />
                  )}
                </div>

                {day.enabled ? (
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <select
                      value={day.startTime}
                      onChange={e => updateDay(index, { startTime: e.target.value })}
                      aria-label={`${day.day} start time`}
                      className={selectClass}
                    >
                      {timeOptionsWith(day.startTime).map(o => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <span className="flex-shrink-0 text-xs text-ink-400">to</span>
                    <select
                      value={day.endTime}
                      onChange={e => updateDay(index, { endTime: e.target.value })}
                      aria-label={`${day.day} end time`}
                      className={`${selectClass} ${invalid ? 'border-danger-500' : ''}`}
                    >
                      {timeOptionsWith(day.endTime).map(o => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <span className="text-sm text-ink-400">Closed</span>
                )}

                {invalid && (
                  <span className="w-full text-xs font-medium text-danger-600">
                    End time must be after the start time
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {workingDays.length === 0 && (
        <Callout tone="warning" icon={Clock}>
          Turn on at least one day so customers have something to book.
        </Callout>
      )}

      <Callout tone="info" icon={CalendarClock}>
        <span className="font-semibold text-ink-800">
          Working hours aren't the same as appointment hours.
        </span>{' '}
        These are the outer boundary of your day. Each booking form sets its own earliest and
        latest appointment times inside them — so you can stop taking appointments at 3:00 PM and
        still work until 5:00 PM.
      </Callout>
    </div>
  );
};
